<template lang="pug">
  .b-spec-table
    //- Шапка вне области горизонтальной прокрутки: внутри неё прилипание по вертикали не работает.
    //- Шапка закреплена под сводом страницы, а её горизонтальный сдвиг повторяет сдвиг таблицы.
    ._head-wrap(ref="head" data-testid="spec-table-head" :style="{ top: stickyTop + 'px' }")
      ._head(:style="{ minWidth: totalWidth + 'px' }")
        ._th(v-for="column in columns" :key="column.key" :style="s(column.key)" :class="{ sortable: !!column.sort }" @click="column.sort && $emit('sort', column.sort)") {{ column.title }}

    ._scroll(ref="scroll" @scroll="syncHead")
      //- окошко аналога выше остальных всплывающих панелей: под ним добавляется место, иначе у нижних строк его обрезает прокрутка таблицы
      ._inner(:style="{ minWidth: totalWidth + 'px' }" :class="{ 'with-popover-room': analogItemId !== null }")
        ._group(v-for="group in groups" :key="group.key" data-testid="spec-group"
          @dragenter="onGroupDragEnter($event, group)"
          @dragover.prevent
          @drop.prevent="onGroupDrop(group)"
          )
          ._group-head
            ._group-bar
              input._group-check(type="checkbox" data-testid="spec-group-checkbox" :checked="isGroupSelected(group)" @change="$emit('select-group', { group, value: $event.target.checked })")
              span._group-drag(v-if="grouping === 'category' && !readOnly" data-testid="spec-group-drag" draggable="true" title="Перетащить категорию"
                @dragstart="onCategoryDragStart($event, group)"
                @dragend="categoryDrag = null"
                ) ⠿
              span._group-title(data-testid="spec-group-title") {{ group.title }}
              span._group-count(data-testid="spec-group-count") {{ group.items.length }} позиций
              span._group-total(v-for="total in totalsOf(group)" :key="total.key")
                span._group-total-label {{ total.label }}
                span._group-total-value(:data-testid="total.testId") {{ money(total.value) }}
              button._group-add(v-if="!readOnly" type="button" data-testid="spec-group-add-item" @click="startNewRow(group)") + Добавить позицию

          template(v-for="item in group.items")
            SpecRow(
              ref="row"
              :key="item.id"
              :item="item"
              :rooms="rooms"
              :readOnly="readOnly"
              :selected="selectedIds.includes(item.id)"
              :editing="editing && editing.itemId === item.id ? editing.field : null"
              :menuOpen="menuItemId === item.id"
              :deliveryEditor="deliveryItemId === item.id"
              :photoMenuOpen="photoItemId === item.id"
              :sourcesOpen="sourcesItemId === item.id"
              :analogOpen="analogItemId === item.id"
              :unlocked="unlockedIds.includes(item.id)"
              :highlighted="highlightedId === item.id"
              @select="$emit('select', { item, value: $event })"
              @edit="onEdit(item, $event)"
              @save="$emit('save', { item, ...$event })"
              @expand="toggleExpand(item)"
              @menu="menuItemId = menuItemId === item.id ? null : item.id"
              @photo-menu="photoItemId = photoItemId === item.id ? null : item.id"
              @photo-file="onPhotoFile(item, $event)"
              @sources="sourcesItemId = sourcesItemId === item.id ? null : item.id"
              @analog="analogItemId = analogItemId === item.id ? null : item.id"
              @delivery-editor="deliveryItemId = $event ? item.id : null"
              @transition="$emit('transition', { item, action: $event })"
              @action="onAction(item, $event)"
              @drag-start="onRowDragStart($event, item, group)"
              @drag-enter="onRowDragEnter($event, item, group)"
              @drag-end="rowDrag = null"
              @drop="onRowDrop(group)"
              )
            SpecItemPanel(
              v-if="expandedId === item.id"
              :key="`panel-${item.id}`"
              :item="item"
              :specificationId="specificationId"
              :readOnly="readOnly"
              @choose="$emit('choose', { item, offer: $event })"
              @change-split="$emit('change-split', { item, offer: $event })"
              @rework="$emit('rework', { item, offer: $event })"
              @open-chat="$emit('open-chat', { item, offer: $event })"
              )

          SpecNewRow(
            v-if="newRow && newRow.groupKey === group.key"
            :key="`new-${group.key}`"
            :defaultCategory="newRow.category"
            :room="newRow.room"
            @save="saveNewRow"
            @cancel="newRow = null"
            )

        SpecNewRow(
          v-if="newRow && !newRow.groupKey"
          :defaultCategory="newRow.category"
          :room="newRow.room"
          @save="saveNewRow"
          @cancel="newRow = null"
          )
</template>

<script>
import SpecRow from '@/components/specification/SpecRow.vue';
import SpecItemPanel from '@/components/specification/SpecItemPanel.vue';
import SpecNewRow from '@/components/specification/SpecNewRow.vue';
import { COLUMNS, TOTAL_WIDTH, colStyle } from '@/components/specification/columns';
import { money } from '@/utils/spec';

/** Итоги в заголовке группы — те же семь показателей, что в своде (ТЗ 6.2, 7.2). */
const GROUP_TOTALS = [
  { key: 'plannedTotal', label: 'План', testId: 'spec-group-total-planned' },
  { key: 'customerTotal', label: 'Заказчику', testId: 'spec-group-total-customer' },
  { key: 'planDeviation', label: 'Отклонение', testId: 'spec-group-total-plan-deviation' },
  { key: 'savingsTotal', label: 'Скидки', testId: 'spec-group-total-savings' },
  { key: 'rewardTotal', label: 'Вознаграждение', testId: 'spec-group-total-reward' },
  { key: 'commissionTotal', label: 'Комиссия', testId: 'spec-group-total-commission' },
  { key: 'designerIncome', label: 'Доход', testId: 'spec-group-total-income' },
];

/**
 * Таблица спецификации: группы (категории или помещения), строки, раскрытая панель позиции,
 * строка новой позиции, перетаскивание (ТЗ 6.2).
 *
 * Перетаскивание реализовано теми же событиями HTML5, что и DraggableList, но своим кодом:
 * DraggableList требует, чтобы событие dragenter приходило на сам перетаскиваемый элемент
 * (`e.target === e.currentTarget && e.target.draggable`), а в строке таблицы курсор всегда
 * находится над ячейкой, поэтому в многоколоночной строке он не срабатывает.
 *
 * Состояния «что сейчас открыто» (правка ячейки, меню строки, редактор срока, список источников,
 * меню фото, раскрытая позиция) живут здесь, а не в строках: каждое из них по ТЗ 6.2 существует
 * на экране в одном экземпляре. Сгенерировано Claude.
 */
export default {
  name: 'SpecTable',
  components: { SpecRow, SpecItemPanel, SpecNewRow },
  props: {
    groups: { type: Array, default: () => [] },
    rooms: { type: Array, default: () => [] },
    selectedIds: { type: Array, default: () => [] },
    unlockedIds: { type: Array, default: () => [] },
    specificationId: { type: Number, required: true },
    grouping: { type: String, default: 'category' },
    /** Архивный проект: все действия записи скрыты (ТЗ 6.1, 6.9). */
    readOnly: Boolean,
    /** Отступ закреплённой шапки сверху — высота закреплённого над таблицей свода. */
    stickyTop: { type: Number, default: 0 },
  },
  data() {
    return {
      columns: COLUMNS,
      totalWidth: TOTAL_WIDTH,
      editing: null,
      menuItemId: null,
      photoItemId: null,
      sourcesItemId: null,
      analogItemId: null,
      deliveryItemId: null,
      expandedId: null,
      /** Позиция, к которой привёл переход «к позиции» (ТЗ 6.8, 6.10): подсвечивается в таблице. */
      highlightedId: null,
      newRow: null,
      rowDrag: null,
      categoryDrag: null,
      dropTarget: null,
    };
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onOutsideClick, true);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onOutsideClick, true);
  },
  methods: {
    money,
    s: colStyle,
    /** Шапка сдвигается по горизонтали вместе с таблицей. */
    syncHead() {
      this.$refs.head.scrollLeft = this.$refs.scroll.scrollLeft;
    },
    /** Итоги группы в порядке показа: значения уже посчитаны (сервером или страницей). */
    totalsOf(group) {
      return GROUP_TOTALS.map((total) => ({ ...total, value: group.total?.[total.key] ?? 0 }));
    },
    onKeydown(event) {
      if (event.key !== 'Escape') return;
      this.closePopovers();
    },
    onOutsideClick(event) {
      if (this.$el.contains(event.target)) return;
      this.closePopovers();
    },
    closePopovers() {
      this.menuItemId = null;
      this.photoItemId = null;
      this.sourcesItemId = null;
      this.analogItemId = null;
      this.deliveryItemId = null;
    },
    onEdit(item, field) {
      this.editing = field ? { itemId: item.id, field } : null;
    },
    /**
     * Подсветка отмечает позицию, к которой привёл переход из уведомления или из чата (ТЗ 6.8):
     * как только пользователь сам раскрывает другую позицию, отмечать больше нечего и подсветка
     * гаснет.
     */
    toggleExpand(item) {
      const open = this.expandedId !== item.id;
      this.expandedId = open ? item.id : null;
      if (open && this.highlightedId !== item.id) this.highlightedId = null;
    },
    /**
     * Раскрыть позицию по идентификатору и показать её: единый переход «к позиции» из панели
     * «Запросы», из события чата и из уведомления (ТЗ 6.2, 6.8, 6.10).
     */
    expandItem(id) {
      this.expandedId = id;
      this.highlightedId = id;
      this.$nextTick(() => {
        const rows = this.$refs.row ?? [];
        const row = rows.find((component) => component.item?.id === id);
        if (row) row.$el.scrollIntoView({ block: 'center' });
      });
    },
    onAction(item, action) {
      this.menuItemId = null;
      this.photoItemId = null;
      this.analogItemId = null;
      this.$emit('action', { item, action });
    },
    onPhotoFile(item, file) {
      this.photoItemId = null;
      this.$emit('photo-file', { item, file });
    },
    isGroupSelected(group) {
      return group.items.length > 0
        && group.items.every((item) => this.selectedIds.includes(item.id));
    },

    /** Новая позиция: в группе-категории категория подставлена, в группе-помещении выбирается. */
    startNewRow(group = null) {
      this.newRow = {
        groupKey: group?.key ?? null,
        category: group?.category ?? '',
        room: group?.room ?? null,
      };
    },
    saveNewRow(row) {
      this.$emit('create-item', row);
      this.newRow = null;
    },

    /** Перетаскивание строк: внутри группы — порядок, в другую группу-помещение — помещение. */
    onRowDragStart(event, item, group) {
      const { dataTransfer } = event;
      dataTransfer.effectAllowed = 'move';
      // без данных Chromium не начинает перенос
      dataTransfer.setData('text/plain', String(item.id));
      this.rowDrag = { item, group };
      this.dropTarget = null;
    },
    onRowDragEnter(event, item, group) {
      if (!this.rowDrag) return;
      event.preventDefault();
      this.dropTarget = { item, group };
    },
    onGroupDragEnter(event, group) {
      if (this.rowDrag && !this.dropTarget) {
        event.preventDefault();
        this.dropTarget = { item: null, group };
        return;
      }
      if (this.categoryDrag) {
        event.preventDefault();
        this.dropTarget = { item: null, group };
      }
    },
    onRowDrop(group) {
      this.finishRowDrop(group);
    },
    onGroupDrop(group) {
      if (this.categoryDrag) {
        this.finishCategoryDrop(group);
        return;
      }
      this.finishRowDrop(group);
    },
    finishRowDrop(group) {
      const drag = this.rowDrag;
      const target = this.dropTarget;
      this.rowDrag = null;
      this.dropTarget = null;
      if (!drag) return;

      const sameGroup = drag.group.key === group.key;

      if (!sameGroup && this.grouping === 'room') {
        this.$emit('move-room', { item: drag.item, room: group.room ?? null });
        return;
      }

      if (!sameGroup) return;

      const ids = group.items.map((i) => i.id).filter((id) => id !== drag.item.id);
      const targetId = target?.item && target.item.id !== drag.item.id ? target.item.id : null;
      const at = targetId === null ? ids.length : ids.indexOf(targetId);
      ids.splice(at < 0 ? ids.length : at, 0, drag.item.id);
      if (ids.join() === group.items.map((i) => i.id).join()) return;
      this.$emit('reorder', { group, ids });
    },
    onCategoryDragStart(event, group) {
      const { dataTransfer } = event;
      dataTransfer.effectAllowed = 'move';
      dataTransfer.setData('text/plain', group.category ?? '');
      this.categoryDrag = group;
      this.dropTarget = null;
    },
    finishCategoryDrop(group) {
      const drag = this.categoryDrag;
      this.categoryDrag = null;
      this.dropTarget = null;
      if (!drag || !drag.category || !group.category || drag.category === group.category) return;
      this.$emit('reorder-categories', { from: drag.category, to: group.category });
    },
  },
};
</script>

<style scoped lang="scss">
.spec-table {
  // ниже свода (z-index 10), но выше строк и их всплывающих элементов внутри прокрутки
  &__head-wrap {
    position: sticky;
    z-index: 9;
    overflow: hidden;
    border: 1px solid var(--color-super-light);
    border-bottom: none;
    border-radius: 8px 8px 0 0;
    background: #F8F9FC;
  }

  &__scroll {
    overflow-x: auto;
    border: 1px solid var(--color-super-light);
    border-top: none;
    border-radius: 0 0 8px 8px;
    background: var(--color-white);
  }

  &__inner {
    display: block;

    &.with-popover-room {
      padding-bottom: 260px;
    }
  }

  &__head {
    display: flex;
    background: #F8F9FC;
    border-bottom: 1px solid var(--color-super-light);
  }

  &__th {
    box-sizing: border-box;
    padding: 8px 6px;
    font-size: 11px;
    font-weight: 700;
    color: var(--color-middle-gray);
    border-right: 1px solid #F3F5F8;

    &.sortable {
      cursor: pointer;
    }
  }

  &__group-head {
    background: #EEF3FA;
    border-bottom: 1px solid var(--color-super-light);
    font-size: 12px;
  }

  // содержимое заголовка прижато к левому краю области прокрутки: сам заголовок шириной во всю
  // таблицу (несколько экранов), и прилипание на нём ничего не даёт — прилипает эта полоса
  &__group-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    width: max-content;
    max-width: 100%;
    position: sticky;
    left: 0;
  }

  &__group-check {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  &__group-drag {
    cursor: grab;
    color: var(--color-light-dark);
    user-select: none;
  }

  &__group-title {
    font-weight: 700;
    font-size: 14px;
  }

  &__group-count,
  &__group-total-label {
    color: var(--color-middle-gray);
  }

  &__group-total {
    display: inline-flex;
    gap: 6px;
  }

  &__group-total-value {
    font-weight: 700;
  }

  &__group-add {
    border: none;
    background: none;
    font: inherit;
    color: var(--color-accent);
    cursor: pointer;
    white-space: nowrap;
  }
}
</style>
